import React, { useRef, useState, useEffect } from 'react';
import { showToast } from '../../../../Component/Toast';
import moment from 'moment';
import Button from '../../../../Component/Button';
// import useClientHook from '../../../../Hooks/useClientHook';

// ─── Field metadata (dice dynamic form) ──────────────────────────────────────
const FIELD_CONFIG = {
    // PERSONAL_INFO
    firstName:              { label: 'First Name',                type: 'text' },
    lastName:               { label: 'Last Name',                 type: 'text' },
    dateOfBirth:            { label: 'Date of Birth',             type: 'date', isDob: true },
    gender:                 { label: 'Gender',                    type: 'select', options: ['MALE', 'FEMALE', 'OTHER'] },
    mobileNumber:           { label: 'Mobile Number',             type: 'tel' },
    email:                  { label: 'Email',                     type: 'email' },
    address:                { label: 'Address',                   type: 'textarea' },
    city:                   { label: 'City',                      type: 'text' },
    state:                  { label: 'State',                     type: 'text' },
    pincode:                { label: 'Pincode',                   type: 'text' },
    nationality:            { label: 'Nationality',               type: 'text' },
    maritalStatus:          { label: 'Marital Status',            type: 'select', options: ['SINGLE', 'MARRIED', 'DIVORCED', 'WIDOWED'] },
    bloodGroup:             { label: 'Blood Group',               type: 'select', options: ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'] },
    emergencyContactName:   { label: 'Emergency Contact Name',    type: 'text' },
    emergencyContactNumber: { label: 'Emergency Contact Number',  type: 'tel' },
    // PAN
    panNumber:              { label: 'PAN Number',                type: 'text' },
    nameOnPan:              { label: 'Name on PAN',               type: 'text' },
    panDob:                 { label: 'Date of Birth (as on PAN)', type: 'date' },
    panDocument:            { label: 'PAN Document',              type: 'file' },
    // PASSPORT
    passportNumber:         { label: 'Passport Number',           type: 'text' },
    surname:                { label: 'Surname',                   type: 'text' },
    givenNames:             { label: 'Given Names',               type: 'text' },
    dateOfIssue:            { label: 'Date of Issue',             type: 'date' },
    dateOfExpiry:           { label: 'Date of Expiry',            type: 'date' },
    placeOfIssue:           { label: 'Place of Issue',            type: 'text' },
    countryOfIssue:         { label: 'Country of Issue',          type: 'text' },
    passportDocument:       { label: 'Passport Document',         type: 'file' },
    // DRIVING_LICENSE
    licenseNumber:          { label: 'License Number',            type: 'text' },
    nameOnLicense:          { label: 'Name on License',           type: 'text' },
    licenseClass:           { label: 'License Class',             type: 'text' },
    issuingAuthority:       { label: 'Issuing Authority',         type: 'text' },
    licenseDocument:        { label: 'License Document',          type: 'file' },
    // DEPENDENT
    dependentName:          { label: "Dependent's Name",          type: 'text' },
    relationship:           { label: 'Relationship',              type: 'select', options: ['SPOUSE', 'CHILD', 'PARENT', 'SIBLING', 'OTHER'] },
};

const SECTION_LABELS = {
    PERSONAL_INFO:   'Personal Information',
    PAN:             'PAN Details',
    PASSPORT:        'Passport Details',
    DRIVING_LICENSE: 'Driving License',
    DEPENDENT:       'Dependent Details',
};

const toLabel = (str) =>
    str.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();

const dobMin = `${moment().year() - 80}-${moment().format('MM-DD')}`;
const dobMax = `${moment().year() - 16}-${moment().format('MM-DD')}`;


// ─── Field renderer (dice form) ───────────────────────────────────────────────
function FieldInput({ sectionKey, field, value, onChange, hasError }) {
    const config = FIELD_CONFIG[field.key] || { label: toLabel(field.key), type: 'text' };
    const disabled = !field.allowEdit;
    const required = field.mandatory;
    const id = `${sectionKey}_${field.key}`;
    const inputClass = `form-control${disabled ? ' bg-light' : ''}${hasError ? ' is-invalid' : ''}`;

    const handleChange = (e) => {
        const val = config.type === 'file' ? e.target.files[0] : e.target.value;
        onChange(sectionKey, field.key, val);
    };

    const renderInput = () => {
        if (config.type === 'select') {
            return (
                <select id={id} className={inputClass} value={value || ''} onChange={handleChange} disabled={disabled} required={required}>
                    <option value=''>Select {config.label}</option>
                    {config.options.map(opt => (
                        <option key={opt} value={opt}>
                            {opt.charAt(0) + opt.slice(1).toLowerCase().replace(/_/g, ' ')}
                        </option>
                    ))}
                </select>
            );
        }
        if (config.type === 'textarea') {
            return <textarea id={id} className={inputClass} value={value || ''} onChange={handleChange} disabled={disabled} required={required} rows={2} />;
        }
        if (config.type === 'file') {
            return (
                <input id={id} type='file' className={inputClass} onChange={handleChange} disabled={disabled} accept='image/*,application/pdf' />
            );
        }
        return (
            <input
                id={id}
                type={config.type}
                className={inputClass}
                value={value || ''}
                onChange={handleChange}
                disabled={disabled}
                required={required}
                style={config.type === 'date' ? { textTransform: 'uppercase' } : undefined}
                {...(config.isDob ? { min: dobMin, max: dobMax } : {})}
            />
        );
    };

    return (
        <div className='text-start mb-3'>
            <label style={{ fontSize: '0.7rem' }} className='form-text text-muted col-12 text-truncate' htmlFor={id}>
                {config.label}{required && <span className='text-danger fw-bold ms-1'>*</span>}
            </label>
            {renderInput()}
            {hasError && <div className='invalid-feedback'>This field is required</div>}
        </div>
    );
}

function Section({ section, values, errors, onChange }) {
    const visibleFields = section.fields.filter(f => f.visible);
    if (!visibleFields.length) return null;

    return (
        <div className='mb-4'>
            <p className='fw-semibold text-start mb-2' style={{ fontSize: '0.85rem', color: '#555', borderBottom: '1px solid #eee', paddingBottom: '4px' }}>
                {SECTION_LABELS[section.key] || toLabel(section.key)}
            </p>
            <div className='row g-2'>
                {visibleFields.map(field => {
                    const config = FIELD_CONFIG[field.key] || {};
                    const colClass = config.type === 'textarea' ? 'col-12' : 'col-md-6 col-12';
                    return (
                        <div key={field.key} className={colClass}>
                            <FieldInput
                                sectionKey={section.key}
                                field={field}
                                value={values[field.key]}
                                onChange={onChange}
                                hasError={errors?.[field.key] === true}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ─── Dice dynamic form ────────────────────────────────────────────────────────
function DiceForm({ onboardingConfig, onSuccess, loading }) {
    const [formValues, setFormValues] = useState({});
    // fieldErrors: { SECTION_KEY: { fieldKey: true } } — true means field has error
    const [fieldErrors, setFieldErrors] = useState({});
    const enabledSections = (onboardingConfig?.sections || []).filter(s => s.enabled);

    useEffect(() => {
        const initial = {};
        enabledSections.forEach(section => {
            initial[section.key] = {};
            section.fields.filter(f => f.visible).forEach(field => {
                initial[section.key][field.key] = '';
            });
        });
        setFormValues(initial);
        setFieldErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onboardingConfig]);

    const handleChange = (sectionKey, fieldKey, value) => {
        setFormValues(prev => ({
            ...prev,
            [sectionKey]: { ...prev[sectionKey], [fieldKey]: value }
        }));
        // Clear error for this field as soon as user fills it
        if (fieldErrors[sectionKey]?.[fieldKey]) {
            setFieldErrors(prev => ({
                ...prev,
                [sectionKey]: { ...prev[sectionKey], [fieldKey]: false }
            }));
        }
    };

    const validate = () => {
        const newErrors = {};
        const missingLabels = [];

        enabledSections.forEach(section => {
            section.fields
                .filter(f => f.visible && f.mandatory && f.allowEdit)
                .forEach(field => {
                    const val = formValues[section.key]?.[field.key];
                    const isEmpty = !val || val === '';
                    if (isEmpty) {
                        if (!newErrors[section.key]) newErrors[section.key] = {};
                        newErrors[section.key][field.key] = true;
                        missingLabels.push(FIELD_CONFIG[field.key]?.label || toLabel(field.key));
                    }
                });
        });

        setFieldErrors(newErrors);

        if (missingLabels.length > 0) {
            showToast({
                type: 'error',
                message: missingLabels.length === 1
                    ? `${missingLabels[0]} is required`
                    : `${missingLabels.length} required fields are missing`
            });
            return false;
        }
        return true;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        const flatData = {};
        enabledSections.forEach(section => {
            Object.entries(formValues[section.key] || {}).forEach(([key, val]) => {
                if (val !== '' && val != null) flatData[key] = val;
            });
        });
        onSuccess(flatData);
    };

    if (!enabledSections.length) {
        return (
            <div className='p-3 text-center' style={cardStyle}>
                <h1 className='display-6 fw-bold mt-2'>Profile Setup</h1>
                <p className='text-muted'>No additional information is required to complete setup.</p>
                <Button onClick={() => onSuccess({})} name='Continue' loading={loading} />
            </div>
        );
    }

    return (
        <div className='p-3 text-center' style={cardStyle}>
            <h1 className='display-6 fw-bold mt-2 text-center'>Profile Setup</h1>
            <p className='text-center text-muted mb-1'>Please fill in your details to complete setup</p>
            <p className='text-end mb-3' style={{ fontSize: '0.75rem', color: '#888' }}>
                <span className='text-danger fw-bold'>*</span> Required field
            </p>
            {enabledSections.map(section => (
                <Section key={section.key} section={section} values={formValues[section.key] || {}} errors={fieldErrors[section.key]} onChange={handleChange} />
            ))}
            <div className='mt-3'>
                <Button onClick={handleSubmit} name='Finish' loading={loading} />
            </div>
        </div>
    );
}

// ─── Original static form (non-dice origins) ──────────────────────────────────
const RELATION_OPTIONS = ['SPOUSE', 'CHILD', 'PARENT', 'SIBLING', 'OTHER'];
const GENDER_OPTIONS = ['MALE', 'FEMALE', 'OTHER'];
const emptyDependent = () => ({ name: '', mobile: '', dob: '', gender: '', relation: '' });
const requiredMark = <span className='text-danger fw-bold ms-1'>*</span>;

function StaticForm({ origin, onSuccess, loading }) {
    const DATE = useRef();
    const GENDER = useRef();
    // const { provider } = useClientHook('LOGIN');
  const showExtraFields = false; // provider === 'FEDERALUAT';

    const [form, setForm] = useState({
        mobile: '',
        fname: '',
        lname: '',
        passportNumber: '',
        passportIssueDate: '',
        passportExpiry: '',
    });
    const [dependents, setDependents] = useState([]);

    const today = moment().format('YYYY-MM-DD');

    useEffect(() => {
        DATE.current.max = dobMax;
        DATE.current.min = dobMin;
    }, []);

    const isEmpOrigin = origin && (origin.includes('employee.dice.tech') || origin.includes('fedgo.dev.eka.io/user-uat') || origin.includes('app.dice.tech'));

    const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    const addDependent = () => setDependents(prev => [...prev, emptyDependent()]);
    const removeDependent = (idx) => setDependents(prev => prev.filter((_, i) => i !== idx));
    const setDependentField = (idx, key, value) =>
        setDependents(prev => prev.map((d, i) => (i === idx ? { ...d, [key]: value } : d)));

    const handleSubmit = () => {
        const gender = GENDER.current.value;
        const dob = DATE.current.value;

        if (!dob && !isEmpOrigin) {
            showToast({ type: 'error', message: 'Select Date of Birth' });
            return;
        }
        if (gender === 'NONE' && !isEmpOrigin) {
            showToast({ type: 'error', message: 'Select Gender' });
            return;
        }

        const data = { avatar: '' };
        if (dob) data.dob = moment(dob, 'YYYY-MM-DD').valueOf();
        if (gender && gender !== 'NONE') data.gender = gender;

        // Extra fields — only for the FEDERALUAT provider
        if (showExtraFields) {
            if (!form.mobile.trim()) {
                showToast({ type: 'error', message: 'Mobile Number is required' });
                return;
            }
            if (!form.fname.trim()) {
                showToast({ type: 'error', message: 'First Name is required' });
                return;
            }
            if (!form.lname.trim()) {
                showToast({ type: 'error', message: 'Last Name is required' });
                return;
            }

            // Passport is optional, but once started every field becomes required
            const passportStarted = !!(form.passportNumber.trim() || form.passportIssueDate || form.passportExpiry);
            if (passportStarted) {
                if (!form.passportNumber.trim()) { showToast({ type: 'error', message: 'Passport Number is required' }); return; }
                if (!form.passportIssueDate) { showToast({ type: 'error', message: 'Passport Issue Date is required' }); return; }
                if (!form.passportExpiry) { showToast({ type: 'error', message: 'Passport Expiry is required' }); return; }
            }

            // Dependents are optional, but each added dependent must be fully filled
            for (let i = 0; i < dependents.length; i++) {
                const d = dependents[i];
                const missing = [];
                if (!d.name.trim()) missing.push('Name');
                if (!d.mobile.trim()) missing.push('Mobile');
                if (!d.dob) missing.push('Date of Birth');
                if (!d.gender) missing.push('Gender');
                if (!d.relation) missing.push('Relation');
                if (missing.length) {
                    showToast({
                        type: 'error',
                        message: `Dependent ${i + 1}: ${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} required`,
                    });
                    return;
                }
            }

            data.mobile = form.mobile.trim();
            data.name = `${form.fname.trim()} ${form.lname.trim()}`.trim();
            data.fname = form.fname.trim();
            data.lname = form.lname.trim();

            if (passportStarted) {
                data.passport = {
                    number: form.passportNumber.trim(),
                    issueDate: moment(form.passportIssueDate, 'YYYY-MM-DD').valueOf(),
                    expiry: moment(form.passportExpiry, 'YYYY-MM-DD').valueOf(),
                };
            }

            if (dependents.length) {
                data.dependents = dependents.map(d => ({
                    name: d.name.trim(),
                    mobile: d.mobile.trim(),
                    dob: moment(d.dob, 'YYYY-MM-DD').valueOf(),
                    gender: d.gender,
                    relation: d.relation,
                }));
            }
        }

        onSuccess(data);
    };

    return (
        <div className='p-3 text-center' style={cardStyle}>
            <h1 className='display-6 fw-bold mt-4'>Profile Setup</h1>
            <p>New user setup, Add Profile photo and fill the details below</p>
            <div className='mt-4 mb-4'>
                <form onSubmit={(e) => e.preventDefault()}>
                    {/* Date of Birth + Gender (unchanged) */}
                    <div className='d-flex w-100 mb-4'>
                        <div className='w-50 me-2 text-start'>
                            <label style={{ fontSize: '0.7rem' }} className='form-text text-muted col-12 text-truncate' htmlFor='DOB_Input'>Date of Birth</label>
                            <input id='DOB_Input' ref={DATE} type='date' className='form-control' style={{ textTransform: 'uppercase' }} />
                        </div>
                        <div className='w-50 text-start'>
                            <label style={{ fontSize: '0.7rem' }} className='form-text text-muted col-12 text-truncate' htmlFor='Gender_Input'>Gender</label>
                            <select id='Gender_Input' ref={GENDER} className='form-control'>
                                <option value='NONE'>Select Gender</option>
                                <option value='MALE'>Male</option>
                                <option value='FEMALE'>Female</option>
                            </select>
                        </div>
                    </div>

                    {/* Extra fields — only for the FEDERALUAT provider */}
                    {showExtraFields && (
                        <>
                            {/* Mobile */}
                            <div className='text-start mb-3'>
                                <label style={{ fontSize: '0.7rem' }} className='form-text text-muted col-12 text-truncate' htmlFor='mobile_Input'>
                                    Mobile Number{requiredMark}
                                </label>
                                <input
                                    id='mobile_Input'
                                    type='tel'
                                    className='form-control'
                                    value={form.mobile}
                                    onChange={(e) => setField('mobile', e.target.value)}
                                    required
                                />
                            </div>

                            {/* Name */}
                            <div className='row g-2 mb-3'>
                                <div className='col-md-6 col-12 text-start'>
                                    <label style={{ fontSize: '0.7rem' }} className='form-text text-muted col-12 text-truncate' htmlFor='fname_Input'>
                                        First Name{requiredMark}
                                    </label>
                                    <input
                                        id='fname_Input'
                                        type='text'
                                        className='form-control'
                                        value={form.fname}
                                        onChange={(e) => setField('fname', e.target.value)}
                                        required
                                    />
                                </div>
                                <div className='col-md-6 col-12 text-start'>
                                    <label style={{ fontSize: '0.7rem' }} className='form-text text-muted col-12 text-truncate' htmlFor='lname_Input'>
                                        Last Name{requiredMark}
                                    </label>
                                    <input
                                        id='lname_Input'
                                        type='text'
                                        className='form-control'
                                        value={form.lname}
                                        onChange={(e) => setField('lname', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Passport (optional) */}
                            <div className='mb-3'>
                                <p className='fw-semibold text-start mb-2' style={{ fontSize: '0.85rem', color: '#555', borderBottom: '1px solid #eee', paddingBottom: '4px' }}>
                                    Passport <span className='text-muted fw-normal'>(optional)</span>
                                </p>
                                <div className='text-start mb-2'>
                                    <label style={{ fontSize: '0.7rem' }} className='form-text text-muted col-12 text-truncate' htmlFor='passportNumber_Input'>Passport Number</label>
                                    <input
                                        id='passportNumber_Input'
                                        type='text'
                                        className='form-control'
                                        value={form.passportNumber}
                                        onChange={(e) => setField('passportNumber', e.target.value)}
                                    />
                                </div>
                                <div className='row g-2'>
                                    <div className='col-md-6 col-12 text-start'>
                                        <label style={{ fontSize: '0.7rem' }} className='form-text text-muted col-12 text-truncate' htmlFor='passportIssue_Input'>Issue Date</label>
                                        <input
                                            id='passportIssue_Input'
                                            type='date'
                                            className='form-control'
                                            style={{ textTransform: 'uppercase' }}
                                            max={today}
                                            value={form.passportIssueDate}
                                            onChange={(e) => setField('passportIssueDate', e.target.value)}
                                        />
                                    </div>
                                    <div className='col-md-6 col-12 text-start'>
                                        <label style={{ fontSize: '0.7rem' }} className='form-text text-muted col-12 text-truncate' htmlFor='passportExpiry_Input'>Expiry Date</label>
                                        <input
                                            id='passportExpiry_Input'
                                            type='date'
                                            className='form-control'
                                            style={{ textTransform: 'uppercase' }}
                                            value={form.passportExpiry}
                                            onChange={(e) => setField('passportExpiry', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Dependents (optional, repeatable) */}
                            <div className='mb-3'>
                                <p className='fw-semibold text-start mb-2' style={{ fontSize: '0.85rem', color: '#555', borderBottom: '1px solid #eee', paddingBottom: '4px' }}>
                                    Dependents <span className='text-muted fw-normal'>(optional)</span>
                                </p>

                                {dependents.map((dep, idx) => (
                                    <div key={idx} className='border rounded p-2 mb-2'>
                                        <div className='d-flex justify-content-between align-items-center mb-2'>
                                            <span className='fw-semibold' style={{ fontSize: '0.8rem', color: '#555' }}>Dependent {idx + 1}</span>
                                            <button type='button' className='btn btn-sm btn-outline-danger py-0 px-2' onClick={() => removeDependent(idx)}>Remove</button>
                                        </div>
                                        <div className='row g-2'>
                                            <div className='col-md-6 col-12 text-start'>
                                                <label style={{ fontSize: '0.7rem' }} className='form-text text-muted col-12 text-truncate'>Name{requiredMark}</label>
                                                <input type='text' className='form-control' value={dep.name} onChange={(e) => setDependentField(idx, 'name', e.target.value)} required />
                                            </div>
                                            <div className='col-md-6 col-12 text-start'>
                                                <label style={{ fontSize: '0.7rem' }} className='form-text text-muted col-12 text-truncate'>Mobile{requiredMark}</label>
                                                <input type='tel' className='form-control' value={dep.mobile} onChange={(e) => setDependentField(idx, 'mobile', e.target.value)} required />
                                            </div>
                                            <div className='col-md-6 col-12 text-start'>
                                                <label style={{ fontSize: '0.7rem' }} className='form-text text-muted col-12 text-truncate'>Date of Birth{requiredMark}</label>
                                                <input type='date' className='form-control' style={{ textTransform: 'uppercase' }} max={today} value={dep.dob} onChange={(e) => setDependentField(idx, 'dob', e.target.value)} required />
                                            </div>
                                            <div className='col-md-6 col-12 text-start'>
                                                <label style={{ fontSize: '0.7rem' }} className='form-text text-muted col-12 text-truncate'>Gender{requiredMark}</label>
                                                <select className='form-control' value={dep.gender} onChange={(e) => setDependentField(idx, 'gender', e.target.value)} required>
                                                    <option value=''>Select Gender</option>
                                                    {GENDER_OPTIONS.map(g => (
                                                        <option key={g} value={g}>{g.charAt(0) + g.slice(1).toLowerCase()}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className='col-md-6 col-12 text-start'>
                                                <label style={{ fontSize: '0.7rem' }} className='form-text text-muted col-12 text-truncate'>Relation{requiredMark}</label>
                                                <select className='form-control' value={dep.relation} onChange={(e) => setDependentField(idx, 'relation', e.target.value)} required>
                                                    <option value=''>Select Relation</option>
                                                    {RELATION_OPTIONS.map(r => (
                                                        <option key={r} value={r}>{r.charAt(0) + r.slice(1).toLowerCase()}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <button type='button' className='btn btn-sm btn-outline-primary w-100' onClick={addDependent}>
                                    + Add Dependent
                                </button>
                            </div>
                        </>
                    )}

                    <Button onClick={handleSubmit} name='Finish' loading={loading} />
                </form>
            </div>
        </div>
    );
}

// ─── Shared card style ────────────────────────────────────────────────────────
const cardStyle = {
    backgroundColor: '#fff',
    borderRadius: '1rem',
    boxShadow: '0 1rem 2rem rgba(0,0,0,.15)',
};

// ─── Main component ───────────────────────────────────────────────────────────
function UpdateProfile({ onboardingConfig, isDice, origin, onSuccess, loading }) {
    return (
        <div className='d-flex align-items-center justify-content-center' style={{ minHeight: '100vh', padding: '2rem 1rem' }}>
            <div style={{ width: '100%', maxWidth: 520 }}>
                {isDice
                    ? <DiceForm onboardingConfig={onboardingConfig} onSuccess={onSuccess} loading={loading} />
                    : <StaticForm origin={origin} onSuccess={onSuccess} loading={loading} />
                }
            </div>
        </div>
    );
}

export default UpdateProfile;
